import dayjs, { Dayjs } from "dayjs";

export type SwayyCouponState = "active" | "inactive" | "expired" | "exhausted";

export type SwayyCouponDiscountType = "percent" | "fixed" | "free_shipping";
export type SwayyCouponChannel = "b2c" | "b2b" | "both";
export type SwayyCouponScope = "all" | "categories" | "products";

/** Matches swayy-backend ymdInIst — coupon schedule is calendar-day only (IST). */
const IST_OFFSET_MS = 330 * 60 * 1000;

const ymdPartsInIst = (
  value: Date,
): { year: number; month: number; day: number } => {
  const shifted = new Date(value.getTime() + IST_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
};

const formatYmd = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/**
 * Persist the picker calendar day as noon UTC so MySQL round-trips do not
 * shift the date when end-of-day IST is stored/read as UTC.
 */
export const toSwayyCouponDateIso = (value: Dayjs): string =>
  `${value.format("YYYY-MM-DD")}T12:00:00.000Z`;

/** Hydrate form date from API using the IST calendar day of the stored instant. */
export const swayyCouponDateToDayjs = (
  value: string | null | undefined,
): Dayjs | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const { year, month, day } = ymdPartsInIst(parsed);
  return dayjs(formatYmd(year, month, day));
};

/** Display coupon start/end as IST calendar date (no time). */
export const formatSwayyCouponDate = (
  value: string | null | undefined,
): string => {
  const asDay = swayyCouponDateToDayjs(value);
  if (!asDay || !asDay.isValid()) return "—";
  return asDay.format("D/M/YYYY");
};

export interface SwayyCoupon {
  id: number;
  code: string;
  description: string | null;
  discount_type: SwayyCouponDiscountType;
  discount_value: number;
  max_discount: number | null;
  min_order_value: number;
  channel: SwayyCouponChannel;
  scope: SwayyCouponScope;
  scope_ids: number[] | null;
  usage_limit: number | null;
  usage_per_user: number;
  first_order_only: boolean;
  starts_at: string | null;
  ends_at: string | null;
  times_redeemed: number;
  is_active: boolean;
  is_expired: boolean;
  state: SwayyCouponState;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface SwayyCouponRedemption {
  id: number;
  coupon_id: number;
  order_id: number;
  user_id: number;
  amount: number;
  created_at: string;
}

export type SwayyCouponActivityStatus = "success" | "failure" | "info";

export interface SwayyCouponActivityLog {
  id: number;
  action: string;
  action_label: string;
  entity_type: string;
  entity_id: number | null;
  status: SwayyCouponActivityStatus;
  admin_id: number;
  admin_name: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface GetSwayyCouponsParams {
  page: number;
  limit: number;
  status: string;
  q: string;
  channel: string;
  starts_from: string;
  starts_to: string;
  ends_from: string;
  ends_to: string;
}

export interface SwayyCouponsPagination {
  total: number;
  page: number;
  limit: number;
}

export interface SwayyCouponsListData {
  coupons: SwayyCoupon[];
  pagination: SwayyCouponsPagination;
}

export interface SwayyCouponBulkExpireResult {
  expired_ids: number[];
  already_expired_ids: number[];
  missing_ids: number[];
}

export interface CreateSwayyCouponPayload {
  code: string;
  description?: string | null;
  discount_type: SwayyCouponDiscountType;
  discount_value: number;
  max_discount?: number | null;
  min_order_value?: number;
  channel?: SwayyCouponChannel;
  scope?: SwayyCouponScope;
  usage_limit?: number | null;
  usage_per_user?: number;
  first_order_only?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active?: boolean;
}

export interface UpdateSwayyCouponPayload {
  description?: string | null;
  discount_type?: SwayyCouponDiscountType;
  discount_value?: number;
  max_discount?: number | null;
  min_order_value?: number;
  channel?: SwayyCouponChannel;
  usage_limit?: number | null;
  usage_per_user?: number;
  first_order_only?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active?: boolean;
  is_expired?: boolean;
}

export const formatSwayyCouponDiscount = (coupon: SwayyCoupon): string => {
  if (coupon.discount_type === "free_shipping") return "Free shipping";
  if (coupon.discount_type === "percent") {
    const cap =
      coupon.max_discount != null ? ` · max ₹${coupon.max_discount}` : "";
    return `${coupon.discount_value}%${cap}`;
  }
  return `₹${coupon.discount_value}`;
};

export const formatSwayyCouponUsage = (coupon: SwayyCoupon): string => {
  const used = coupon.times_redeemed ?? 0;
  if (coupon.usage_limit == null) return `${used} / Unlimited`;
  return `${used} / ${coupon.usage_limit}`;
};

export const isSwayyCouponDateElapsed = (
  value: string | null | undefined,
): boolean => {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  const valueYmd = ymdPartsInIst(parsed);
  const todayYmd = ymdPartsInIst(new Date());
  const valueKey = valueYmd.year * 10000 + valueYmd.month * 100 + valueYmd.day;
  const todayKey = todayYmd.year * 10000 + todayYmd.month * 100 + todayYmd.day;
  return valueKey < todayKey;
};

/** Operational status only — expiry lives in its own column. */
export const getSwayyCouponDisplayState = (
  coupon: Pick<
    SwayyCoupon,
    "is_active" | "usage_limit" | "times_redeemed" | "state"
  >,
): Exclude<SwayyCouponState, "expired"> => {
  if (!coupon.is_active) return "inactive";
  if (coupon.state === "exhausted") return "exhausted";
  if (coupon.state === "active" || coupon.state === "inactive") {
    return coupon.state;
  }

  if (
    coupon.usage_limit != null &&
    coupon.times_redeemed >= coupon.usage_limit
  ) {
    return "exhausted";
  }
  return "active";
};
